import { useState } from "react";
import { X, Trash2, Minus, Plus, Loader2, ShoppingBag, Lock, Tag } from "lucide-react";
import { Sheet, SheetContent, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { useCart } from "@/lib/cart";
import { api, resolveImg } from "@/lib/api";
import { toast } from "sonner";

const money = (n) => `$${Number(n || 0).toFixed(2)}`;

export const CartDrawer = () => {
  const { items, open, setOpen, setQty, remove, subtotal, shipping } = useCart();
  const [checkingOut, setCheckingOut] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "" });
  const [codeInput, setCodeInput] = useState("");
  const [disc, setDisc] = useState(null); // { code, kind, value }
  const [checkingCode, setCheckingCode] = useState(false);

  const hasShipping = shipping > 0;
  const discount = disc ? Math.min(disc.kind === "percent" ? (subtotal * disc.value) / 100 : disc.value, subtotal) : 0;
  const total = subtotal - discount + shipping;

  const applyCode = async () => {
    const code = codeInput.trim();
    if (!code) return;
    setCheckingCode(true);
    try {
      const { data } = await api.post("/store/discount/validate", { code, subtotal_cents: Math.round(subtotal * 100) });
      if (data.valid) {
        setDisc({ code: data.code, kind: data.kind, value: data.value });
        toast.success(`Code ${data.code} applied`);
      } else {
        setDisc(null);
        toast.error("That discount code isn't valid.");
      }
    } catch {
      toast.error("Couldn't check that code. Please try again.");
    } finally {
      setCheckingCode(false);
    }
  };

  const pay = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim()) {
      toast.error("Please enter your name and email.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/payments/checkout", {
        items: items.map((i) => ({ product_id: i.productId, size: i.size, color: i.color, personalization: i.personalization, personalization_image: i.personalizationImage, quantity: i.quantity })),
        customer_name: form.name.trim(),
        customer_email: form.email.trim(),
        customer_phone: form.phone.trim(),
        origin_url: window.location.origin,
        discount_code: disc?.code || "",
      });
      // Redirect to the Authorize.Net secure hosted payment page via an auto-submitted form POST
      const f = document.createElement("form");
      f.method = "POST";
      f.action = data.hosted_url;
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "token";
      input.value = data.token;
      f.appendChild(input);
      document.body.appendChild(f);
      f.submit();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Couldn't start checkout. Please try again.");
      setLoading(false);
    }
  };

  return (
    <Sheet open={open} onOpenChange={setOpen}>
      <SheetContent side="right" className="bg-paper border-none w-[92vw] sm:w-[440px] p-0 flex flex-col" data-testid="cart-drawer">
        <SheetTitle className="sr-only">Your shopping cart</SheetTitle>
        <SheetDescription className="sr-only">Review the items in your cart and check out securely.</SheetDescription>
        <div className="bg-ink text-[#f2f0ea] px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShoppingBag size={18} className="text-gold" />
            <span className="font-display text-xl tracking-[0.04em]">{checkingOut ? "CHECKOUT" : "YOUR CART"}</span>
          </div>
          <button onClick={() => setOpen(false)} data-testid="cart-close" className="text-white/70 hover:text-white"><X size={20} /></button>
        </div>

        {items.length === 0 ? (
          <div className="flex-1 flex flex-col items-center justify-center text-navy/50 gap-3 px-6">
            <ShoppingBag size={40} className="text-navy/20" />
            <p className="text-sm">Your cart is empty.</p>
          </div>
        ) : (
          <>
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4" data-testid="cart-items">
              {items.map((i) => (
                <div key={i.lineId} className="flex gap-3 border-b border-line pb-4" data-testid={`cart-item-${i.productId}`}>
                  <div className="w-16 h-16 bg-[#e9e6dd] shrink-0 overflow-hidden">
                    <img src={resolveImg(i.img)} alt={i.name} className="w-full h-full object-cover" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className="text-sm font-semibold text-ink leading-tight">{i.name}</p>
                      <button onClick={() => remove(i.lineId)} data-testid={`cart-remove-${i.productId}`} className="text-navy/40 hover:text-red-600 shrink-0"><Trash2 size={15} /></button>
                    </div>
                    {(i.size || i.color) && (
                      <p className="text-[11px] text-navy/50 mt-0.5">{[i.size, i.color].filter(Boolean).join(" · ")}</p>
                    )}
                    {i.personalization && (
                      <p className="text-[11px] text-gold mt-0.5" data-testid={`cart-pers-${i.productId}`}>✎ "{i.personalization}"{i.personalizationFee ? ` (+$${Number(i.personalizationFee).toFixed(2)})` : ""}</p>
                    )}
                    {i.personalizationImage && (
                      <div className="flex items-center gap-1.5 mt-0.5" data-testid={`cart-pers-img-${i.productId}`}>
                        <img src={resolveImg(i.personalizationImage)} alt="Custom" className="w-6 h-6 object-cover border border-line" />
                        <span className="text-[11px] text-gold">Photo added{!i.personalization && i.personalizationFee ? ` (+$${Number(i.personalizationFee).toFixed(2)})` : ""}</span>
                      </div>
                    )}
                    <p className="text-[11px] text-navy/50">{i.fulfillment === "ship" ? "Ships to you" : "School pickup"}</p>
                    <div className="flex items-center justify-between mt-2">
                      <div className="flex items-center border border-line">
                        <button onClick={() => setQty(i.lineId, i.quantity - 1)} data-testid={`cart-dec-${i.productId}`} className="px-2 py-1 text-navy/60 hover:text-ink"><Minus size={13} /></button>
                        <span className="px-2 text-sm w-8 text-center" data-testid={`cart-qty-${i.productId}`}>{i.quantity}</span>
                        <button onClick={() => setQty(i.lineId, i.quantity + 1)} data-testid={`cart-inc-${i.productId}`} className="px-2 py-1 text-navy/60 hover:text-ink"><Plus size={13} /></button>
                      </div>
                      <span className="text-sm font-semibold">{money((i.unitPrice ?? i.price) * i.quantity)}</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="border-t border-line bg-white px-6 py-5 space-y-3">
              {disc ? (
                <div className="flex items-center justify-between border border-green-700/30 bg-green-50 px-3 py-2" data-testid="cart-discount-applied">
                  <span className="flex items-center gap-1.5 text-green-800 text-xs font-bold tracking-[0.08em]"><Tag size={13} /> {disc.code} APPLIED</span>
                  <button onClick={() => { setDisc(null); setCodeInput(""); }} data-testid="cart-discount-remove" className="text-navy/40 hover:text-red-600"><X size={14} /></button>
                </div>
              ) : (
                <div className="flex gap-2">
                  <input value={codeInput} onChange={(e) => setCodeInput(e.target.value.toUpperCase())} data-testid="cart-discount-input" placeholder="DISCOUNT CODE"
                    className="flex-1 border border-line px-3 py-2.5 text-sm outline-none focus:border-navy tracking-[0.06em]" />
                  <button onClick={applyCode} disabled={checkingCode || !codeInput.trim()} data-testid="cart-discount-apply"
                    className="bg-navy text-white px-4 text-xs font-bold tracking-[0.1em] hover:bg-ink transition-colors disabled:opacity-50 flex items-center gap-1.5">
                    {checkingCode ? <Loader2 size={13} className="animate-spin" /> : "APPLY"}
                  </button>
                </div>
              )}
              <div className="flex justify-between text-sm text-navy/70"><span>Subtotal</span><span>{money(subtotal)}</span></div>
              {disc && <div className="flex justify-between text-sm text-green-700 font-semibold"><span>Discount ({disc.code})</span><span data-testid="cart-discount-amount">-{money(discount)}</span></div>}
              {hasShipping && <div className="flex justify-between text-sm text-navy/70"><span>Shipping</span><span>{money(shipping)}</span></div>}
              <div className="flex justify-between text-base font-bold text-ink pt-1 border-t border-line"><span>Total</span><span data-testid="cart-total">{money(total)}</span></div>
              <p className="text-[11px] text-navy/40">Taxes calculated securely at checkout.</p>

              {!checkingOut ? (
                <button onClick={() => setCheckingOut(true)} data-testid="cart-checkout-button"
                  className="w-full bg-gold text-ink py-3.5 text-xs font-bold tracking-[0.16em] hover:bg-gold-soft transition-colors">
                  CHECKOUT
                </button>
              ) : (
                <form onSubmit={pay} className="space-y-3 pt-1" data-testid="checkout-form">
                  <input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="checkout-name" placeholder="Full name"
                    className="w-full border border-line px-4 py-3 text-sm outline-none focus:border-navy bg-paper" />
                  <input value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="checkout-email" type="email" placeholder="Email address"
                    className="w-full border border-line px-4 py-3 text-sm outline-none focus:border-navy bg-paper" />
                  <input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="checkout-phone" placeholder="Phone (optional)"
                    className="w-full border border-line px-4 py-3 text-sm outline-none focus:border-navy bg-paper" />
                  <button type="submit" disabled={loading} data-testid="checkout-pay-button"
                    className="w-full bg-ink text-[#f2f0ea] py-3.5 text-xs font-bold tracking-[0.16em] hover:bg-navy transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                    {loading ? <><Loader2 size={15} className="animate-spin" /> REDIRECTING…</> : <><Lock size={14} /> PAY {money(total)}</>}
                  </button>
                  <button type="button" onClick={() => setCheckingOut(false)} className="w-full text-navy/50 text-xs hover:text-navy py-1">← Back to cart</button>
                </form>
              )}
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
};
